import { usesHostedBilling } from '@/lib/appDistribution';
import React, { useEffect, useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useBillingPalette } from '@/components/Settings/billing/BillingGroup';
import { AUTO_TOPUP_BONUS_PERCENT } from '@/lib/autoTopupOffer';
import { trackEvent } from '@/constants/utils/analytics';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

// This is a discovery surface. Setup and its explicit consent stay in the
// existing configuration flow; a store visit never changes billing settings.
export default function CreditStoreTopUpCard({ onClose, onUpgrade, compact = false }) {
  const palette = useBillingPalette();
  const styles = useMemo(() => createStyles(palette, compact), [palette, compact]);
  const user = useAuthStore(state => state.user);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { usesWhopCheckout } = useGabPurchase();
  const isPlus = !!user?.flags?.isPlus;
  const enabled = !!user?.autoTopup?.enabled;
  const visible = !!user && usesHostedBilling()
    && (!usesWhopCheckout || !!user?.autoTopup?.whopAvailable);

  useEffect(() => {
    if (visible && !enabled) trackEvent('conversion', 'auto_topup_nudge_shown', 'credit_store');
  }, [visible, enabled]);

  if (!visible) return null;

  const handlePress = () => {
    trackEvent('conversion', 'auto_topup_nudge_clicked', 'credit_store');
    if (!isPlus) return onUpgrade?.();
    onClose?.();
    closeModal();
    openModal('AutoTopUpConfigModal');
  };

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.icon}><Icon name="arrows-rotate" size={18} color={palette.ink} /></View>
        <View style={styles.badge}><Text size="xxs" weight="bold">{enabled ? 'Enabled' : `+${AUTO_TOPUP_BONUS_PERCENT}% credits`}</Text></View>
      </View>
      <View style={styles.copy}>
        <Text size="l" weight="bold">{enabled ? 'Auto top-up is on' : 'Keep your ideas flowing.'}</Text>
        <Text size="s" color="secondary" style={styles.description}>
          {enabled
            ? `Your balance refills automatically, with ${AUTO_TOPUP_BONUS_PERCENT}% extra credits on every top-up.`
            : `Auto top-up adds credits when you run low, with ${AUTO_TOPUP_BONUS_PERCENT}% extra every time.`}
        </Text>
      </View>
      {!compact && <View style={styles.benefits}>
        {['Choose your credit pack', 'Set a monthly spending limit', 'Turn it off anytime'].map(label => (
          <View key={label} style={styles.benefit}><Icon name="check" size={11} color={palette.ink} /><Text size="xs" color="secondary">{label}</Text></View>
        ))}
      </View>}
      <Pressable accessibilityRole="button" onPress={handlePress}
        style={({ hovered, pressed }) => [styles.button, { opacity: pressed ? 0.7 : hovered ? 0.85 : 1 }]}>
        <Text size="s" weight="bold" style={{ flexShrink: 1, textAlign: 'center' }}>{enabled ? 'Manage auto top-up' : isPlus ? 'Set up auto top-up' : 'Explore auto top-up with Plus'}</Text>
        <Icon name="arrow-right" size={12} color={palette.ink} />
      </Pressable>
      <Text size="xxs" color="secondary" style={styles.footnote}>
        {compact ? 'Choose your pack and monthly limit. Turn off anytime.' : enabled ? 'Adjust your settings or turn it off anytime.' : isPlus ? 'You review the details before enabling.' : 'Auto top-up is included with Plus.'}
      </Text>
    </View>
  );
}

const createStyles = (palette, compact) => StyleSheet.create({
  card: { padding: compact ? 18 : 22, borderRadius: 20, backgroundColor: palette.groupFill, borderWidth: 1, borderColor: palette.groupBorder, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  icon: { width: 38, height: 38, borderRadius: 12, backgroundColor: palette.tileFill, alignItems: 'center', justifyContent: 'center' },
  badge: { paddingHorizontal: 9, paddingVertical: 5, borderRadius: 20, backgroundColor: palette.tileFill },
  copy: { gap: 7 },
  description: { lineHeight: 20 },
  benefits: { gap: 10 },
  benefit: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  button: { minHeight: 44, paddingHorizontal: 12, paddingVertical: 10, borderRadius: 12, backgroundColor: palette.colors.solid_color_primary, borderWidth: 1, borderColor: palette.groupBorder, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8 },
  footnote: { textAlign: 'center', lineHeight: 16 },
});
